import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api.ts';
import { formatEuros } from '../format.ts';
import { fmtMinutes, type HoursSnapshot } from '../ratings.ts';

const ERRORS: Record<string, string> = {
  NO_DIVERGENCE: 'Les heures pointées sont dans la tolérance : rien à contester.',
  ALREADY_OPENED: 'Un ticket existe déjà pour cette mission.',
  NOT_YOUR_CONCESSION: 'Ce choix ne peut être fait que par l’autre partie.',
  DISPUTE_CLOSED: 'Ce ticket est déjà réglé.',
  MISSION_NOT_ELIGIBLE: 'Les heures ne peuvent plus être contestées : la mission est payée.',
  INVALID_NOTE: 'Écrivez au moins quelques mots.',
};

/**
 * Heures prévues, heures pointées et médiation, pour les deux parties. Le paiement attend tant qu'un ticket est ouvert.
 * `onChanged` est appelé quand l'état de la mission ou du paiement peut avoir bougé.
 */
export function HoursPanel({ missionId, side, status, onChanged }: { missionId: string; side: 'ORG' | 'EXTRA'; status: string; onChanged?: () => void }) {
  const [h, setH] = useState<HoursSnapshot | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<HoursSnapshot>(`/hours/missions/${missionId}`).then(setH).catch(() => setH(null));
  }, [missionId]);
  useEffect(load, [load, status]);

  async function run(action: () => Promise<unknown>, success?: string) {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await action();
      if (success) setInfo(success);
      setNote('');
      load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Action impossible.' : 'Action impossible.');
    } finally {
      setBusy(false);
    }
  }

  if (!h || h.actualMinutes === null || !['COMPLETED', 'VALIDATED', 'CLOSED'].includes(status)) return null;
  const d = h.dispute;
  if (!h.divergent && !d) return null;

  const amount = (minutes: number) => formatEuros(Math.round((h.hourlyRateCents * minutes) / 60));
  // Celui à qui un choix coûte est le seul à pouvoir y consentir.
  const canPlanned = d && ((d.kind === 'LESS' && side === 'ORG') || (d.kind === 'MORE' && side === 'EXTRA'));
  const canActual = d && ((d.kind === 'LESS' && side === 'EXTRA') || (d.kind === 'MORE' && side === 'ORG'));
  const myNote = d ? (side === 'ORG' ? d.orgNote : d.extraNote) : null;

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontWeight: 800 }}>Heures de la mission</div>
      <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
        Prévues : <strong>{fmtMinutes(h.plannedMinutes)}</strong> ({amount(h.plannedMinutes)}) · Pointées : <strong>{fmtMinutes(h.actualMinutes)}</strong> ({amount(h.actualMinutes)})
      </div>
      {error && <div className="error-banner">{error}</div>}
      {info && <div className="badge badge-available" style={{ padding: '10px 14px' }}>{info}</div>}

      {!d && (
        <>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            L'écart dépasse {h.toleranceMinutes} minutes. {side === 'ORG' ? 'Sans contestation, le montant prévu est réglé.' : 'Sans contestation, le montant prévu est versé.'} Un désaccord se règle par un ticket de médiation ; le paiement est suspendu le temps de le régler.
          </div>
          {status !== 'CLOSED' && (
            <>
              <textarea className="input" rows={2} maxLength={1000} placeholder="Votre version (facultatif)" value={note} onChange={(e) => setNote(e.target.value)} />
              <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => run(() => api.post(`/hours/missions/${missionId}/dispute`, { note: note || undefined }), 'Ticket ouvert : le paiement est suspendu jusqu’à décision.')}>
                Contester les heures
              </button>
            </>
          )}
        </>
      )}

      {d && d.status === 'OPEN' && (
        <>
          <div className="badge badge-pending" style={{ padding: '10px 14px', lineHeight: 1.5 }}>
            Ticket de médiation ouvert : le paiement est suspendu. Vous pouvez vous accorder ci-dessous, sinon l'équipe Mon Extra tranche sous 24 h.
          </div>
          {d.orgNote && <div style={{ fontSize: 13 }}><strong>Établissement :</strong> {d.orgNote}</div>}
          {d.extraNote && <div style={{ fontSize: 13 }}><strong>Extra :</strong> {d.extraNote}</div>}
          <textarea className="input" rows={2} maxLength={1000} placeholder={myNote ? 'Mettre à jour ma version' : 'Ma version des faits'} value={note} onChange={(e) => setNote(e.target.value)} />
          <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={busy || note.trim().length < 3} onClick={() => run(() => api.post(`/hours/disputes/${d.id}/note`, { note }), 'Votre version est enregistrée.')}>
            Enregistrer ma version
          </button>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canPlanned && <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => run(() => api.post(`/hours/disputes/${d.id}/concede`, { choice: 'PLANNED' }), 'Réglé sur les heures prévues.')}>Accepter les heures prévues ({fmtMinutes(d.plannedMinutes)})</button>}
            {canActual && <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => run(() => api.post(`/hours/disputes/${d.id}/concede`, { choice: 'ACTUAL' }), 'Réglé sur les heures pointées.')}>Accepter les heures pointées ({fmtMinutes(d.actualMinutes)})</button>}
          </div>
        </>
      )}

      {d && d.status === 'RESOLVED' && (
        <div className="badge badge-available" style={{ padding: '10px 14px', lineHeight: 1.5 }}>
          Réglé : {fmtMinutes(d.billableMinutes ?? 0)} retenues ({amount(d.billableMinutes ?? 0)}).
          {d.resolution === 'SUPPORT' && d.decisionNote ? ` Décision de l'équipe : ${d.decisionNote}` : ' Accord entre les parties.'}
        </div>
      )}
    </div>
  );
}
